import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { colors } from "../styles/colors";
import { typography } from "../styles/typography";
import { globalStyles } from "../styles/globalStyles";
import bookingAPI from "../services/bookingApi";
import { useUser } from "../context/UserContext";

// Responsive helper functions (adapted for web)
const getResponsiveValue = (small, medium, large) => {
  const width = window.innerWidth;
  if (width >= 1024) return large;
  if (width >= 768) return medium;
  return small;
};

const isDesktop = window.innerWidth >= 1024;
const isLargeDesktop = window.innerWidth >= 1200;

const getColumns = () => {
  if (isLargeDesktop) return 3;
  if (isDesktop) return 2;
  return 1;
};

const ViewAllBookingsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: userLoading } = useUser();
  
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Enhanced filter and sort states
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  useEffect(() => {
    if (!userLoading && (!isAuthenticated || !user?.is_admin)) {
      alert('Please log in as an administrator to access this page.');
      navigate('/');
      return;
    }
    if (!userLoading && isAuthenticated && user?.is_admin) {
      loadBookings();
    }
  }, [isAuthenticated, userLoading, user, navigate]);

  useEffect(() => {
    applyFiltersAndSort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, statusFilter, sortBy, searchQuery, dateFilter]);

  const loadBookings = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await bookingAPI.getAllBookings();
      // console.log('📡 API Response:', response);

      if (response.success) {
        setBookings(response.data || []);
      } else {
        alert('Failed to load bookings');
        console.error('API Error:', response.error);
      }
    } catch (error) {
      console.error('💥 Network Error:', error);
      alert('Network error. Please check connection.');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...bookings];

    // Apply date filter
    if (dateFilter) {
      filtered = filtered.filter(booking =>
        booking.booking_date && booking.booking_date.startsWith(dateFilter)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => 
        booking.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(booking => 
        booking.event_title?.toLowerCase().includes(query) ||
        booking.eventTitle?.toLowerCase().includes(query) ||
        booking.requester_name?.toLowerCase().includes(query) ||
        booking.requesterName?.toLowerCase().includes(query) ||
        booking.name?.toLowerCase().includes(query) ||
        booking.hall_name?.toLowerCase().includes(query) ||
        booking.hallName?.toLowerCase().includes(query) ||
        booking.hall?.name?.toLowerCase().includes(query) ||
        booking.email?.toLowerCase().includes(query)||
        booking.timeSlot?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at || b.submittedAt || Date.now()) - 
                 new Date(a.created_at || a.submittedAt || Date.now());
        case 'oldest':
          return new Date(a.created_at || a.submittedAt || Date.now()) - 
                 new Date(b.created_at || b.submittedAt || Date.now());
        case 'event_date':
          return new Date(b.booking_date) - new Date(a.booking_date);
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        case 'hall':
          return (a.hall_name || '').localeCompare(b.hall_name || '');
        default:
          return 0;
      }
    });

    setFilteredBookings(filtered);
  };

  const handleBookingAction = async (bookingId, action, reason = null) => {
    try {
      setActionLoading(true);
      
      const response = await bookingAPI.updateBookingStatus(bookingId, action, reason);
      
      if (response.success) {
        alert(`Booking ${action} successfully!`);
        await loadBookings();
        setModalVisible(false);
        setSelectedBooking(null);
        setRejectReason("");
      } else {
        alert(`Failed to ${action} booking: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`Error ${action} booking:`, error);
      alert(`Error ${action} booking. Please try again.`);
    } finally {
      setActionLoading(false);
    }
  };

  const openBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setModalVisible(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
      case 'approved': return colors.success;
      case 'rejected': return colors.danger;
      case 'pending': return colors.warning;
      case 'cancelled': return colors.gray;
      default: return colors.textLight;
    }
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'pending': return '⏳';
      case 'cancelled': return '🚫';
      default: return '❓';
    }
  };

  // Show loading while checking authentication
  if (userLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Verifying admin access...</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Loading all bookings...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.scrollView}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div>
              <div style={styles.title}>📋 All Bookings</div>
              <div style={styles.subtitle}>
                Manage and track all booking requests ({filteredBookings.length} found)
              </div>
            </div>
            <button style={styles.backButton} onClick={() => navigate('/admin')}>
              <span style={styles.backButtonText}>← Back to Dashboard</span>
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div style={styles.filtersContainer}>
          <div style={styles.searchContainer}>
            <input
              style={styles.searchInput}
              type="text"
              placeholder="Search bookings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div style={styles.filtersRow}>
            <input
              type="date"
              style={styles.filterSelect}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              title="Filter by booking date"
            />
            {dateFilter && (
              <button style={styles.clearDateButton} onClick={() => setDateFilter('')}>
                Clear Date
              </button>
            )}
            <select
              style={styles.filterSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              style={styles.filterSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="event_date">Event Date (Newest First)</option>
              <option value="status">By Status</option>
              <option value="hall">By Hall</option>
            </select>

            <button
              style={styles.refreshButton}
              onClick={() => loadBookings(true)}
              disabled={refreshing}
            >
              <span style={styles.refreshButtonText}>
                {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
              </span>
            </button>
          </div>
        </div>

        {/* Bookings Grid */}
        <div style={styles.bookingsContainer}>
          {filteredBookings.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📭</div>
              <div style={styles.emptyTitle}>No bookings found</div>
              <div style={styles.emptySubtitle}>
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your filters or search terms'
                  : 'No booking requests have been submitted yet'
                }
              </div>
            </div>
          ) : (
            <div style={styles.bookingsGrid}>
              {filteredBookings.map((booking) => (
                <div key={booking.id} style={styles.bookingCard}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardTitle}>
                      {booking.event_title || booking.eventTitle || 'Untitled Event'}
                    </div>
                    <div style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(booking.status)
                    }}>
                      <span style={styles.statusText}>
                        {getStatusIcon(booking.status)} {booking.status || 'Unknown'}
                      </span>
                    </div>
                  </div>

                  <div style={styles.cardContent}>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>📅 Date:</span>
                      <span style={styles.detailValue}>{formatDate(booking.booking_date)}</span>
                    </div>
                    
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>🏛️ Hall:</span>
                      <span style={styles.detailValue}>{booking.hall_name || booking.hallName || booking.hall?.location || 'N/A'}</span>
                    </div>
                    
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>👤 Requester:</span>
                      <span style={styles.detailValue}>{booking.requester_name || booking.requesterName || booking.name || 'N/A'}</span>
                    </div>
                    
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>👥 Attendees:</span>
                      <span style={styles.detailValue}>{booking.attendees || 0} people</span>
                    </div>
                    
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>📧 Email:</span>
                      <span style={{...styles.detailValue, ...styles.emailText}}>
                        {booking.email || 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div style={styles.cardActions}>
                    <button
                      style={styles.viewButton}
                      onClick={() => openBookingDetails(booking)}
                    >
                      <span style={styles.viewButtonText}>👁️ View Details</span>
                    </button>
                    
                    {booking.status === 'pending' && (
                      <div style={styles.actionButtons}>
                        <button
                          style={styles.approveButton}
                          onClick={() => handleBookingAction(booking.id, 'approved')}
                          disabled={actionLoading}
                        >
                          <span style={styles.approveButtonText}>✅ Approve</span>
                        </button>
                        <button
                          style={styles.rejectButton}
                          onClick={() => {
                            setSelectedBooking(booking);
                            setModalVisible(true);
                          }}
                          disabled={actionLoading}
                        >
                          <span style={styles.rejectButtonText}>❌ Reject</span>
                        </button>
                      </div>
                    )}
                    
                    {booking.status !== 'pending' && booking.status !== 'rejected' && (
                      <button
                        style={styles.rejectButton}
                        onClick={() => {
                          setSelectedBooking(booking);
                          setModalVisible(true);
                        }}
                        disabled={actionLoading}
                      >
                        <span style={styles.rejectButtonText}>❌ Reject</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Booking Details Modal */}
      {modalVisible && selectedBooking && (
        <div style={styles.modalOverlay} onClick={() => setModalVisible(false)}>
          <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                {selectedBooking.status === 'pending' ? 'Booking Details & Action' : 
                 selectedBooking.status === 'rejected' ? 'Booking Details' : 'Booking Details & Reject'}
              </div>
              <button
                style={styles.closeButton}
                onClick={() => setModalVisible(false)}
              >
                ×
              </button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.modalDetailRow}>
                <strong>Event Title:</strong> {selectedBooking.event_title || selectedBooking.eventTitle || 'N/A'}
              </div>
              <div style={styles.modalDetailRow}>
                <strong>Date:</strong> {formatDate(selectedBooking.booking_date)}
              </div>
              <div style={styles.modalDetailRow}>
                <strong>Time:</strong> {selectedBooking.timeSlot || 'N/A'}
              </div>
              <div style={styles.modalDetailRow}>
                <strong>Hall:</strong> {selectedBooking.hall_name || selectedBooking.hallName || selectedBooking.hall?.location || 'N/A'}
              </div>
              <div style={styles.modalDetailRow}>
                <strong>Requester:</strong> {selectedBooking.requester_name || selectedBooking.requesterName || selectedBooking.name || 'N/A'}
              </div>
              <div style={styles.modalDetailRow}>
                <strong>Email:</strong> {selectedBooking.email || 'N/A'}
              </div>
              <div style={styles.modalDetailRow}>
                <strong>Phone:</strong> {selectedBooking.phone || 'N/A'}
              </div>
              <div style={styles.modalDetailRow}>
                <strong>Attendees:</strong> {selectedBooking.attendees || 0} people
              </div>
              <div style={styles.modalDetailRow}>
                <strong>Organization:</strong> {selectedBooking.organization || 'N/A'}
              </div>
              {selectedBooking.purpose && (
                <div style={styles.modalDetailRow}>
                  <strong>Purpose:</strong> {selectedBooking.purpose}
                </div>
              )}
              <div style={styles.modalDetailRow}>
                <strong>Status:</strong> 
                <span style={{
                  ...styles.modalStatus,
                  color: getStatusColor(selectedBooking.status)
                }}>
                  {getStatusIcon(selectedBooking.status)} {selectedBooking.status || 'Unknown'}
                </span>
              </div>

              {(selectedBooking.status === 'pending' || selectedBooking.status === 'approved') && (
                <div style={styles.rejectSection}>
                  <label style={styles.rejectLabel}>
                    Rejection Reason:
                  </label>
                  <textarea
                    style={styles.rejectTextarea}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    rows={3}
                  />
                </div>
              )}
            </div>

            <div style={styles.modalActions}>
              <button
                style={styles.modalCancelButton}
                onClick={() => setModalVisible(false)}
              >
                Close
              </button>
              
              {selectedBooking.status === 'pending' && (
                <>
                  <button
                    style={styles.modalApproveButton}
                    onClick={() => handleBookingAction(selectedBooking.id, 'approved')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : '✅ Approve'}
                  </button>
                  <button
                    style={styles.modalRejectButton}
                    onClick={() => handleBookingAction(selectedBooking.id, 'rejected', rejectReason)}
                    disabled={actionLoading || !rejectReason.trim()}
                  >
                    {actionLoading ? 'Processing...' : '❌ Reject'}
                  </button>
                </>
              )}
              
              {selectedBooking.status === 'approved' && (
                <button
                  style={styles.modalRejectButton}
                  onClick={() => handleBookingAction(selectedBooking.id, 'rejected', rejectReason)}
                  disabled={actionLoading || !rejectReason.trim()}
                >
                  {actionLoading ? 'Processing...' : '❌ Reject Booking'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.background,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
    overflowY: 'auto',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  spinner: {
    width: 50,
    height: 50,
    border: `4px solid ${colors.border}`,
    borderTop: `4px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    ...typography.body,
    color: colors.textLight,
    marginTop: 16,
  },
  header: {
    backgroundColor: colors.primary,
    padding: getResponsiveValue(20, 24, 32),
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: getResponsiveValue(24, 28, 32),
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: 8,
  },
  subtitle: {
    ...typography.body,
    color: colors.white,
    opacity: 0.9,
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: getResponsiveValue(8, 10, 12),
    borderRadius: getResponsiveValue(6, 8, 10),
    border: 'none',
    cursor: 'pointer',
  },
  backButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
  },
  filtersContainer: {
    backgroundColor: colors.white,
    padding: getResponsiveValue(16, 20, 24),
    borderBottom: `1px solid ${colors.border}`,
  },
  searchContainer: {
    marginBottom: getResponsiveValue(12, 16, 20),
  },
  searchInput: {
    width: '100%',
    padding: getResponsiveValue(10, 12, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    boxSizing: 'border-box',
    outline: 'none',
  },
  filtersRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: getResponsiveValue(8, 12, 16),
    flexWrap: 'wrap',
  },
  filterSelect: {
    padding: getResponsiveValue(8, 10, 12),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    backgroundColor: colors.white,
    cursor: 'pointer',
    outline: 'none',
  },
  clearDateButton: {
    padding: getResponsiveValue(8, 10, 12),
    border: `1px solid ${colors.danger}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(13, 14, 16),
    color: colors.danger,
    backgroundColor: colors.white,
    cursor: 'pointer',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  refreshButton: {
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    padding: getResponsiveValue(8, 10, 12),
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    cursor: 'pointer',
    fontWeight: '600',
  },
  refreshButtonText: {
    color: colors.white,
  },
  bookingsContainer: {
    padding: getResponsiveValue(16, 20, 32),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: getResponsiveValue(40, 60, 80),
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: getResponsiveValue(48, 60, 72),
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  emptyTitle: {
    fontSize: getResponsiveValue(18, 20, 24),
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: getResponsiveValue(8, 10, 12),
  },
  emptySubtitle: {
    ...typography.body,
    color: colors.textLight,
    maxWidth: 400,
  },
  bookingsGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: getResponsiveValue(16, 20, 24),
  },
  bookingCard: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(8, 12, 16),
    padding: getResponsiveValue(16, 20, 24),
    flex: getColumns() === 3 ? '0 0 calc(33.333% - 16px)' : getColumns() === 2 ? '0 0 calc(50% - 16px)' : '0 0 100%',
    minWidth: 300,
    ...globalStyles.shadow,
    border: `1px solid ${colors.border}`,
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: getResponsiveValue(12, 16, 20),
  },
  cardTitle: {
    fontSize: getResponsiveValue(16, 18, 20),
    fontWeight: 'bold',
    color: colors.text,
    flex: 1,
    marginRight: 12,
    lineHeight: 1.4,
  },
  statusBadge: {
    paddingLeft: getResponsiveValue(8, 10, 12),
    paddingRight: getResponsiveValue(8, 10, 12),
    paddingTop: getResponsiveValue(4, 5, 6),
    paddingBottom: getResponsiveValue(4, 5, 6),
    borderRadius: 16,
  },
  statusText: {
    color: colors.white,
    fontSize: getResponsiveValue(10, 11, 12),
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  cardContent: {
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: getResponsiveValue(6, 8, 10),
    gap: 12,
  },
  detailLabel: {
    ...typography.small,
    color: colors.textLight,
    fontWeight: '500',
    minWidth: 80,
    flex: '0 0 auto',
  },
  detailValue: {
    ...typography.small,
    color: colors.text,
    fontWeight: '600',
    textAlign: 'right',
    flex: 1,
  },
  emailText: {
    fontSize: getResponsiveValue(11, 12, 14),
    wordBreak: 'break-word',
  },
  cardActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: getResponsiveValue(8, 10, 12),
  },
  viewButton: {
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    padding: getResponsiveValue(8, 10, 12),
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    cursor: 'pointer',
    fontWeight: '600',
    width: '100%',
  },
  viewButtonText: {
    color: colors.white,
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: getResponsiveValue(8, 10, 12),
  },
  approveButton: {
    backgroundColor: colors.success,
    color: colors.white,
    border: 'none',
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: getResponsiveValue(4, 6, 8),
    fontSize: getResponsiveValue(12, 14, 16),
    cursor: 'pointer',
    fontWeight: '600',
    flex: 1,
  },
  approveButtonText: {
    color: colors.white,
  },
  rejectButton: {
    backgroundColor: colors.danger,
    color: colors.white,
    border: 'none',
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: getResponsiveValue(4, 6, 8),
    fontSize: getResponsiveValue(12, 14, 16),
    cursor: 'pointer',
    fontWeight: '600',
    flex: 1,
  },
  rejectButtonText: {
    color: colors.white,
  },

  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContainer: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(12, 16, 20),
    padding: getResponsiveValue(20, 24, 32),
    minWidth: getResponsiveValue(320, 480, 600),
    maxWidth: '90%',
    maxHeight: '90%',
    overflowY: 'auto',
    ...globalStyles.shadow,
  },
  modalHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: getResponsiveValue(20, 24, 32),
    paddingBottom: getResponsiveValue(12, 16, 20),
    borderBottom: `1px solid ${colors.border}`,
  },
  modalTitle: {
    fontSize: getResponsiveValue(18, 20, 24),
    fontWeight: 'bold',
    color: colors.text,
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: 24,
    color: colors.textLight,
    cursor: 'pointer',
    width: 32,
    height: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    marginBottom: getResponsiveValue(20, 24, 32),
  },
  modalDetailRow: {
    marginBottom: getResponsiveValue(12, 16, 20),
    lineHeight: 1.6,
  },
  modalStatus: {
    marginLeft: 8,
    fontWeight: 'bold',
  },
  rejectSection: {
    marginTop: getResponsiveValue(20, 24, 32),
    paddingTop: getResponsiveValue(16, 20, 24),
    borderTop: `1px solid ${colors.border}`,
  },
  rejectLabel: {
    display: 'block',
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
    color: colors.text,
    marginBottom: getResponsiveValue(8, 10, 12),
  },
  rejectTextarea: {
    width: '100%',
    minHeight: getResponsiveValue(60, 80, 100),
    padding: getResponsiveValue(10, 12, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: getResponsiveValue(10, 12, 16),
    paddingTop: getResponsiveValue(16, 20, 24),
    borderTop: `1px solid ${colors.border}`,
  },
  modalCancelButton: {
    backgroundColor: colors.gray,
    color: colors.white,
    border: 'none',
    padding: getResponsiveValue(10, 12, 16),
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    cursor: 'pointer',
    fontWeight: '600',
  },
  modalApproveButton: {
    backgroundColor: colors.success,
    color: colors.white,
    border: 'none',
    padding: getResponsiveValue(10, 12, 16),
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    cursor: 'pointer',
    fontWeight: '600',
  },
  modalRejectButton: {
    backgroundColor: colors.danger,
    color: colors.white,
    border: 'none',
    padding: getResponsiveValue(10, 12, 16),
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    cursor: 'pointer',
    fontWeight: '600',
  },
};

export default ViewAllBookingsPage;